<script setup lang="ts">
import type { PropertyType } from '@/backend/types'
import IconSprite from '@/uiKit/IconSprite.vue'
import ToolTip from '@/uiKit/ToolTip.vue'

import { FIELD_TYPES_LABELS } from '@/modules/WorkspaceSettings/propertyConfig'
import BadgeItem from '@/uiKit/BadgeItem.vue'
import Menu from '@/uiKit/Menu'
import PopOver from '@/uiKit/PopOver.vue'
import { computed, ref, useTemplateRef } from 'vue'

import { TYPE_ICON } from './icons'
import ProjectTableHeaderAddPropertyAuto from './ProjectTableHeaderAddPropertyAuto.vue'
import PropertyTypeTooltip from './PropertyTypeTooltip.vue'
import { useFieldTypeOptions } from './useFieldTypeOptions'

const emit = defineEmits<{ (e: 'create-property', type: PropertyType): void }>()

const menuIsOpen = ref(false)

const fieldTypesOptions = useFieldTypeOptions()

const onSelect = (type: PropertyType) => {
  menuIsOpen.value = false
  emit('create-property', type)
}

const onOpenChange = (isOpen: boolean) => {
  menuIsOpen.value = isOpen
  search.value = ''
}

const escapeId = (id: string) => CSS.escape(id)

const showAutoProperty = ref(false)

const anchorEl = useTemplateRef('anchorEl')

defineOptions({
  inheritAttrs: false,
})

const search = ref('')
const filteredPropertyTypes = computed(() => {
  return fieldTypesOptions.value.filter((o) =>
    o.data.label.toLowerCase().includes(search.value.toLowerCase()),
  )
})
</script>

<template>
  <Menu.Root
    v-slot="{ getTriggerProps }"
    :positioning="{ placement: 'bottom-start', offset: { mainAxis: 0, crossAxis: 1 } }"
    close-on-select
    @change:open="onOpenChange"
  >
    <ToolTip
      title="Add property"
      :auto-placement-options="{ allowedPlacements: ['top-end'] }"
    >
      <button
        v-bind="{ ...getTriggerProps(), ...$attrs }"
        ref="anchorEl"
        class="size-8"
        aria-label="Add a property"
      >
        <IconSprite
          id="add-property"
          icon="plus"
          size="sm"
          class="size-full text-icon-subtle"
          data-test="add-property"
        />
      </button>
    </ToolTip>
    <Menu.Content
      class="min-w-56"
      data-test="add-property-menu"
    >
      <Menu.ControlledSearch v-model="search" />
      <Menu.Divider />
      <Menu.Item
        icon="ai-sparkles"
        label="Auto"
        @select="showAutoProperty = true"
      >
        <template #prefix="{ active, menuItemDomId }">
          <PopOver
            :open="active"
            :target-selector="`#${escapeId(menuItemDomId)}`"
            :auto-placement-options="{ allowedPlacements: ['left-start', 'right-start'] }"
            :popover-container-attrs="{ class: '[&_[role=dialog]]:pointer-events-none' }"
          >
            <!-- 👆 pointer-events-none here because the popover intercepts events in CI -->
            <!--
                Likely, the popover positioning is flashing between left-start and right-start in CI.
                Tweaking that would be a long term fix.
              -->
            <template #content>
              <PropertyTypeTooltip type="auto" />
            </template>
          </PopOver>
        </template>
      </Menu.Item>
      <Menu.Divider v-if="filteredPropertyTypes.length > 0" />
      <Menu.Item
        v-for="item in filteredPropertyTypes"
        :key="item.id"
        role="menuitem"
        :label="FIELD_TYPES_LABELS[item.id]"
        :icon="TYPE_ICON[item.id]"
        @select="onSelect(item.id)"
      >
        <template #prefix="{ active, menuItemDomId }">
          <PopOver
            :open="active"
            :target-selector="`#${escapeId(menuItemDomId)}`"
            :auto-placement-options="{ allowedPlacements: ['left-start', 'right-start'] }"
            :popover-container-attrs="{ class: '[&_[role=dialog]]:pointer-events-none' }"
          >
            <!-- 👆 pointer-events-none here because the popover intercepts events in CI -->
            <!--
              Likely, the popover positioning is flashing between left-start and right-start in CI.
              Tweaking that would be a long term fix.
            -->
            <template #content>
              <PropertyTypeTooltip :type="item.id" />
            </template>
          </PopOver>
        </template>
        <template
          v-if="item.id === 'number'"
          #suffix
        >
          <BadgeItem
            label="New"
            size="xs"
            variant="blue"
          />
        </template>
      </Menu.Item>
    </Menu.Content>
  </Menu.Root>
  <PopOver
    v-if="showAutoProperty"
    open
    :auto-placement-options="{ allowedPlacements: ['bottom-start', 'bottom-end'] }"
    :offset="{ mainAxis: 0, crossAxis: 1 }"
    :target-selector="anchorEl"
  >
    <template #content>
      <ProjectTableHeaderAddPropertyAuto
        :anchor-ref="anchorEl"
        class="w-80"
        @close="showAutoProperty = false"
      />
    </template>
  </PopOver>
</template>
